import { toaster } from "@/components/ui/toaster"
import { Tooltip } from "@/components/ui/tooltip"
import { useGenerateRationale } from "@/hooks/useGenerateRationale"
import { Button, IconButton } from "@chakra-ui/react"
import { useState } from "react"
import { FiZap } from "react-icons/fi"
import { RationalePreviewModal } from "./RationalePreviewModal"

interface GenerateRationaleButtonProps {
  questionText: string
  options: string[]
  correctAnswer: number
  questionId?: string
  subjectIds?: string[]
  topicIds?: string[]
  onRationaleGenerated: (rationaleHtml: string) => void
  variant?: "button" | "icon"
  disabled?: boolean
}

export function GenerateRationaleButton({
  questionText,
  options,
  correctAnswer,
  questionId,
  subjectIds,
  topicIds,
  onRationaleGenerated,
  variant = "button",
  disabled = false,
}: GenerateRationaleButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [generatedRationale, setGeneratedRationale] = useState<{
    html: string
    provider?: string
    model?: string
    estimatedCost?: number
  } | null>(null)

  const generateMutation = useGenerateRationale()

  const handleGenerate = () => {
    // Validate required fields
    if (!questionText.trim()) {
      toaster.create({
        title: "Question text required",
        description: "Please enter a question before generating a rationale.",
        type: "error",
      })
      return
    }

    if (options.length < 2 || options.some((opt) => !opt.trim())) {
      toaster.create({
        title: "Answer options required",
        description: "Please provide at least 2 valid answer options.",
        type: "error",
      })
      return
    }

    if (correctAnswer < 0 || correctAnswer >= options.length) {
      toaster.create({
        title: "Correct answer required",
        description: "Please select a correct answer before generating.",
        type: "error",
      })
      return
    }

    // Call API
    generateMutation.mutate(
      {
        questionText,
        options,
        correctAnswer,
        questionId,
        subjectIds,
        topicIds,
      },
      {
        onSuccess: (data) => {
          setGeneratedRationale({
            html: data.rationale_html,
            provider: data.provider,
            model: data.model,
            estimatedCost: data.estimated_cost || undefined,
          })
          setIsModalOpen(true)
        },
        onError: (error: Error) => {
          toaster.create({
            title: "Generation failed",
            description:
              error.message ||
              "Failed to generate rationale. Please try again.",
            type: "error",
            action: {
              label: "Retry",
              onClick: handleGenerate,
            },
          })
        },
      },
    )
  }

  const handleAccept = () => {
    if (generatedRationale) {
      onRationaleGenerated(generatedRationale.html)
      setIsModalOpen(false)
      toaster.create({
        title: "Rationale added",
        description:
          "AI-generated rationale has been added. You can edit it further if needed.",
        type: "success",
      })
    }
  }

  const handleRegenerate = () => {
    handleGenerate()
  }

  const tooltipContent = disabled
    ? "Enter question text and select a correct answer first"
    : "Generate explanation using AI (considers question, all options, and metadata)"

  if (variant === "icon") {
    return (
      <>
        <Tooltip content={tooltipContent}>
          <IconButton
            aria-label="Generate rationale with AI"
            size="sm"
            variant="outline"
            colorPalette="purple"
            onClick={handleGenerate}
            loading={generateMutation.isPending}
            disabled={disabled || generateMutation.isPending}
          >
            <FiZap />
          </IconButton>
        </Tooltip>

        {generatedRationale && (
          <RationalePreviewModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            rationaleHtml={generatedRationale.html}
            isLoading={generateMutation.isPending}
            onAccept={handleAccept}
            onRegenerate={handleRegenerate}
            provider={generatedRationale.provider}
            model={generatedRationale.model}
            estimatedCost={generatedRationale.estimatedCost}
          />
        )}
      </>
    )
  }

  return (
    <>
      <Tooltip content={tooltipContent}>
        <Button
          size="sm"
          variant="outline"
          colorPalette="purple"
          onClick={handleGenerate}
          loading={generateMutation.isPending}
          disabled={disabled || generateMutation.isPending}
        >
          <FiZap />
          Generate with AI
        </Button>
      </Tooltip>

      {generatedRationale && (
        <RationalePreviewModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          rationaleHtml={generatedRationale.html}
          isLoading={generateMutation.isPending}
          onAccept={handleAccept}
          onRegenerate={handleRegenerate}
          provider={generatedRationale.provider}
          model={generatedRationale.model}
          estimatedCost={generatedRationale.estimatedCost}
        />
      )}
    </>
  )
}
